
/**
 * @file GeneralFeatureAnnotationCard.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Jul 12, 2022 20:56
 */
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';

import { findLabel, findHelpText } from 'src/@common/schemaUtilities';

import ClassificationCodeField from 'src/components/fieldComponents/ClassificationCodeField';
import RenderAnnotationFields from 'src/content/DocumentAnnotator/AnnotationGroup/RenderAnnotationFields';
import VeriomeTextField from 'src/components/fieldComponents/VeriomeTextField';
import { objectExists, stringExists } from 'src/@common/utilities';

function GeneralFeatureAnnotationCard(props) {
    const {
        biocurations,
        dataLoading,
        diseaseOptions,
        classificationOptions,
        formFields,
        fieldSchema,
        handleBlur,
        handleChange,
        handleDynamicFieldChange,
        handleVariantObjAdd,
        handleVariantObjBlur,
        handleVariantObjDelete,
        handleGDotLookup,
    } = props;

    const fieldErrors = {};

    let canRenderDynamicFields = false;
    if (stringExists(formFields.classificationCode) &&
        objectExists(formFields.fields)) {
        canRenderDynamicFields = true;
    }

    return (

        <>

            <Box>

                <VeriomeTextField
                    fieldErrors={fieldErrors}
                    fieldLabel={findLabel(fieldSchema, 'revisedText')}
                    fieldName={'revisedText'}
                    currentValue={formFields.revisedText ? formFields.revisedText : ''}
                    helpText={findHelpText(fieldSchema, 'revisedText')}
                    isFocus={false}
                    isFullWidth={true}
                    isMultiline={true}
                    isRequired={false}
                    isDisabled={false}
                    handleBlur={() => {
                        handleBlur('revisedText');
                    }}
                    handleChange={(event) => {
                        handleChange(event, 'revisedText');
                    }}
                />

            </Box>

            <Box>

                <ClassificationCodeField
                    fieldName={'classificationCode'}
                    currentValue={formFields.classificationCode ? formFields.classificationCode : ''}
                    options={classificationOptions}
                    fieldErrors={fieldErrors}
                    fieldLabel={findLabel(fieldSchema, 'classificationCode')}
                    helpText={findHelpText(fieldSchema, 'classificationCode')}
                    isFocus={false}
                    inputSxStyle={{
                        ml: '0px',
                    }}
                    handleBlur={() => {
                        // no onBlur handler required.  redux is updated
                        // from server when geneSymbol is submitted.
                    }}
                    handleChange={(event, fieldName) => {
                        handleChange(event, fieldName);
                    }}
                />

            </Box>

            {canRenderDynamicFields &&
                <Box sx={{ marginLeft: '8px', marginTop: '8px' }}>

                    <RenderAnnotationFields
                        annotationType={formFields.annotationType}
                        biocurations={biocurations}
                        dataLoading={dataLoading}
                        diseaseOptions={diseaseOptions}
                        fieldErrors={fieldErrors}
                        formFields={formFields.fields}
                        fieldSchema={fieldSchema}
                        handleBlur={handleBlur}
                        handleChange={handleDynamicFieldChange}
                        handleVariantObjAdd={handleVariantObjAdd}
                        handleVariantObjBlur={handleVariantObjBlur}
                        handleVariantObjDelete={handleVariantObjDelete}
                        handleGDotLookup={handleGDotLookup}
                    />
                </Box>
            }

            {canRenderDynamicFields &&
                <Box sx={{ marginLeft: '2px', marginTop: '8px' }}>
                    <VeriomeTextField
                        key={'internalNote-field'}
                        fieldErrors={fieldErrors}
                        fieldLabel={findLabel(fieldSchema, 'internalNote')}
                        fieldName={'internalNote'}
                        currentValue={formFields.internalNote ? formFields.internalNote : ''}
                        helpText={findHelpText(fieldSchema, 'internalNote')}
                        isFocus={false}
                        isFullWidth={true}
                        isRequired={false}
                        isMultiline={true}
                        sxStyle={{
                            mr: '10px'
                        }}
                        handleBlur={() => {
                            handleBlur('internalNote');
                        }}
                        handleChange={(event) => {
                            handleChange(event, 'internalNote');
                        }}
                    />

                </Box>
            }

        </ >
    )
}

GeneralFeatureAnnotationCard.propTypes = {
    biocurations: PropTypes.object.isRequired,
    classificationOptions: PropTypes.array.isRequired,
    diseaseOptions: PropTypes.array,
    dataLoading: PropTypes.bool,
    formFields: PropTypes.object.isRequired,
    fieldSchema: PropTypes.array.isRequired,
    handleBlur: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleDynamicFieldChange: PropTypes.func.isRequired,
    handleVariantObjAdd: PropTypes.func.isRequired,
    handleVariantObjBlur: PropTypes.func.isRequired,
    handleVariantObjDelete: PropTypes.func.isRequired,
};

export default GeneralFeatureAnnotationCard;